import { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Star, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RewardsPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [rewardsCard, setRewardsCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/rewards/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      if (!response.ok) throw new Error('Failed to lookup rewards');

      const data = await response.json();
      setRewardsCard(data);
      setSearched(true);
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error('Failed to look up rewards');
    } finally {
      setLoading(false);
    }
  };

  const renderPunchCard = () => {
    if (!rewardsCard) return null;

    const stamps = rewardsCard.stamps;
    const punchSlots = [];

    // 10 regular slots + 1 free drink slot
    for (let i = 0; i < 10; i++) {
      punchSlots.push(
        <motion.div
          key={i}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: i * 0.05 }}
          className={`punch-circle ${i < stamps ? 'stamped' : 'empty'}`}
          data-testid={`punch-slot-${i}`}
        >
          {i < stamps && (
            <svg className="w-6 h-6 md:w-8 md:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          )}
        </motion.div>
      );
    }

    // Free drink slot
    punchSlots.push(
      <motion.div
        key="free"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5 }}
        className={`w-12 h-12 md:w-16 md:h-16 rounded-full border-3 border-sip-yellow flex items-center justify-center
          ${stamps >= 10 ? 'bg-sip-yellow' : 'bg-white'}`}
        data-testid="punch-slot-free"
      >
        <Star className={stamps >= 10 ? 'text-gray-900' : 'text-gray-300'} size={24} />
      </motion.div>
    );

    return punchSlots;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="rewards-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-yellow to-sip-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Gift className="w-16 h-16 mx-auto mb-4 text-gray-900" />
            <h1 className="font-script text-4xl md:text-6xl text-gray-900 mb-4">
              Rewards
            </h1>
            <p className="text-xl text-gray-800 max-w-2xl mx-auto">
              Buy 10 drinks, get your 11th FREE!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Rewards Lookup */}
      <section className="py-16">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lookup Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleLookup}
            className="card-graffiti mb-8"
            data-testid="rewards-lookup-form"
          >
            <h2 className="font-semibold text-xl text-gray-900 mb-4 text-center">
              Check Your Rewards
            </h2>
            <div className="flex gap-3">
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
                className="input-graffiti flex-1"
                data-testid="input-phone"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary shrink-0 px-4"
                data-testid="lookup-btn"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
              </button>
            </div>
          </motion.form>

          {/* Punch Card Display */}
          {searched && rewardsCard && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-graffiti"
              data-testid="punch-card"
            >
              <div className="text-center mb-6">
                <h3 className="font-semibold text-xl text-gray-900">Your Punch Card</h3>
                <p className="text-gray-600">
                  {rewardsCard.stamps}/10 stamps collected
                </p>
              </div>

              {/* Punch Card Grid */}
              <div className="grid grid-cols-6 gap-2 md:gap-4 justify-items-center mb-6">
                {renderPunchCard()}
              </div>

              {/* Free Drinks Counter */}
              {rewardsCard.free_drinks_earned > 0 && (
                <div className="bg-sip-purple text-white p-4 rounded-2xl text-center">
                  <p className="font-semibold text-lg">
                    You have {rewardsCard.free_drinks_earned} FREE drink{rewardsCard.free_drinks_earned > 1 ? 's' : ''} available!
                  </p>
                </div>
              )}

              {/* How it works */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-lg text-gray-900 mb-3">How It Works</h4>
                <ul className="text-gray-600 space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="bg-sip-cyan text-white w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-sm font-bold">1</span>
                    Give us your phone number when you order
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-sip-cyan text-white w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-sm font-bold">2</span>
                    Earn a stamp with every drink purchase
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-sip-cyan text-white w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-sm font-bold">3</span>
                    Collect 10 stamps and get your 11th drink FREE!
                  </li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Info Card (shown before search) */}
          {!searched && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-featured"
              data-testid="rewards-info"
            >
              <Star className="w-12 h-12 mx-auto mb-4 text-sip-purple" />
              <h3 className="font-semibold text-xl text-gray-900 text-center mb-4">
                Join Our Rewards Program!
              </h3>
              <p className="text-gray-600 text-center mb-6">
                It's easy! Just give us your phone number when you order, and we'll track your stamps automatically.
              </p>
              <div className="bg-white p-4 rounded-xl text-center">
                <p className="font-script text-2xl text-sip-purple">
                  10 Drinks = 1 FREE!
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RewardsPage;
